import { IonIcon } from '@ionic/react';
import {
  alertCircle,
  checkmarkCircle,
  closeCircle,
  pauseCircle,
  refreshCircle,
} from 'ionicons/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import ui from '../../offline-ui.module.css';
import { OfflineCacheStatus } from '../../sync-job/offline-cache-status';

export type OfflineCacheStatusProps = {
  status: OfflineCacheStatus;
};

function formatSyncDate(at: number, language: string): string | null {
  if (at <= 0) {
    return null;
  }
  const locale = language.startsWith('fr') ? 'fr' : 'en';
  return dayjs(at).locale(locale).format('D MMMM YYYY · HH:mm');
}

type StatusVisual = {
  cardClass: string;
  icon: string;
  iconClass: string;
  headline: string;
};

export function OfflineCacheStatusBlock({ status }: OfflineCacheStatusProps) {
  const { t, i18n } = useTranslation(['common'], { useSuspense: false });

  let visual: StatusVisual;
  let detail: string | null = null;
  let chips: { label: string; value: string }[] = [];

  switch (status.kind) {
    case 'none':
      visual = {
        cardClass: ui.cacheCardUnavailable,
        icon: closeCircle,
        iconClass: ui.cacheIconMuted,
        headline: t('common:sync_job.cache_status_headline_none'),
      };
      detail =
        status.reason === 'no_scope'
          ? t('common:sync_job.cache_status_none_scope')
          : t('common:sync_job.cache_status_none');
      break;

    case 'in_progress': {
      const started = formatSyncDate(status.startedAt, i18n.language);
      visual = {
        cardClass: ui.cacheCardProgress,
        icon: refreshCircle,
        iconClass: ui.cacheIconProgress,
        headline: t('common:sync_job.cache_status_headline_progress'),
      };
      detail = started
        ? t('common:sync_job.cache_status_started_at', { date: started })
        : t('common:sync_job.cache_status_in_progress_short');
      break;
    }

    case 'paused': {
      const updated = formatSyncDate(status.updatedAt, i18n.language);
      visual = {
        cardClass: ui.cacheCardWarning,
        icon: pauseCircle,
        iconClass: ui.cacheIconWarning,
        headline: t('common:sync_job.cache_status_headline_paused'),
      };
      detail = updated
        ? t('common:sync_job.cache_status_paused_at', { date: updated })
        : t('common:sync_job.cache_status_paused_short');
      break;
    }

    case 'stale':
      visual = {
        cardClass: ui.cacheCardWarning,
        icon: alertCircle,
        iconClass: ui.cacheIconWarning,
        headline: t('common:sync_job.cache_status_headline_stale'),
      };
      detail = t('common:sync_job.cache_status_stale');
      break;

    case 'ready': {
      const dateStr = status.legacyDate
        ? null
        : formatSyncDate(status.syncedAt, i18n.language);
      const withErrors = status.withErrors;

      visual = {
        cardClass: withErrors ? ui.cacheCardWarning : ui.cacheCardReady,
        icon: withErrors ? alertCircle : checkmarkCircle,
        iconClass: withErrors ? ui.cacheIconWarning : ui.cacheIconReady,
        headline: withErrors
          ? t('common:sync_job.cache_status_headline_partial')
          : t('common:sync_job.cache_status_headline_ready'),
      };

      if (withErrors) {
        detail = dateStr
          ? t('common:sync_job.cache_status_errors_at', {
              count: status.errorCount,
              date: dateStr,
            })
          : t('common:sync_job.cache_status_ready_errors_short', {
              count: status.errorCount,
            });
      } else {
        detail = dateStr
          ? t('common:sync_job.cache_status_synced_at', { date: dateStr })
          : t('common:sync_job.cache_status_legacy');
      }

      chips = [
        {
          label: t('common:sync_job.cache_chip_plots'),
          value: String(status.plots),
        },
        {
          label: t('common:sync_job.cache_chip_analyses'),
          value: String(status.analyses),
        },
        {
          label: t('common:sync_job.cache_chip_mode'),
          value: status.includeHeatmaps
            ? t('common:sync_job.cache_status_ready_images')
            : t('common:sync_job.cache_status_ready_no_images'),
        },
      ];
      break;
    }
  }

  return (
    <div className={`${ui.cacheCard} ${visual.cardClass}`}>
      <IonIcon
        className={`${ui.cacheIcon} ${visual.iconClass}`}
        icon={visual.icon}
      />
      <div className={ui.cacheBody}>
        <p className={ui.cacheHeadline}>{visual.headline}</p>
        {detail ? <p className={ui.cacheDetail}>{detail}</p> : null}
        {chips.length > 0 ? (
          <ul className={ui.cacheChips}>
            {chips.map((chip) => (
              <li key={chip.label} className={ui.cacheChip}>
                <span className={ui.cacheChipValue}>{chip.value}</span>
                <span className={ui.cacheChipLabel}>{chip.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export default OfflineCacheStatusBlock;
